import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, KeyRound, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnerRegisterPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [code, setCode] = useState(() => (params.get('code') || '').toUpperCase());
  const [validating, setValidating] = useState(false);
  const [codeStatus, setCodeStatus] = useState(null); // null | 'valid' | 'invalid'
  const [codeReason, setCodeReason] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Validate code on mount (if pre-filled) and on blur
  const validateCode = async (value) => {
    const v = (value || '').trim().toUpperCase();
    if (!v) {
      setCodeStatus(null);
      setCodeReason('');
      return;
    }
    setValidating(true);
    setCodeStatus(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/partner/invite-codes/validate/${encodeURIComponent(v)}`);
      if (r.data?.valid) {
        setCodeStatus('valid');
        setCodeReason('');
      } else {
        setCodeStatus('invalid');
        const reasonMap = {
          not_found: 'Código no encontrado',
          already_used: 'Este código ya fue utilizado',
          revoked: 'Este código fue revocado por el administrador',
          missing: 'Ingresá el código',
        };
        setCodeReason(reasonMap[r.data?.reason] || 'Código inválido');
      }
    } catch (e) {
      setCodeStatus('invalid');
      setCodeReason('No se pudo validar el código');
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    if (code) validateCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (codeStatus !== 'valid') {
      toast.error('Validá tu código de invitación antes de continuar.');
      return;
    }
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/partner/register`, {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
        invite_code: code.trim().toUpperCase(),
      });
      toast.success('¡Cuenta creada! Iniciá sesión para continuar.');
      navigate('/login?partner=1', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No se pudo crear la cuenta.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="partner-register-page">
        <div className="flex items-center justify-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-950 text-center mb-1">Registro de Partner</h1>
        <p className="text-sm text-gray-500 text-center mb-6">
          Ingresá el código de invitación que te entregó ACBotHub para crear tu cuenta.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="code">Código de invitación *</Label>
            <div className="relative">
              <Input
                id="code"
                value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeStatus(null); }}
                onBlur={(e) => validateCode(e.target.value)}
                placeholder="PARTNER-XXXXXXXX"
                required
                data-testid="partner-invite-code-input"
                className={`font-mono pr-10 ${codeStatus === 'valid' ? 'border-emerald-400' : codeStatus === 'invalid' ? 'border-red-400' : ''}`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {validating ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  : codeStatus === 'valid' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-testid="code-valid-icon" />
                  : codeStatus === 'invalid' ? <XCircle className="w-4 h-4 text-red-500" data-testid="code-invalid-icon" />
                  : null}
              </div>
            </div>
            {codeStatus === 'invalid' && codeReason && (
              <p className="text-xs text-red-600 mt-1" data-testid="code-error-message">{codeReason}</p>
            )}
            {codeStatus === 'valid' && (
              <p className="text-xs text-emerald-600 mt-1">Código válido ✓</p>
            )}
          </div>

          <div>
            <Label htmlFor="name">Nombre</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre" data-testid="partner-name-input" />
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input id="email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="partner@empresa.com" data-testid="partner-email-input" />
          </div>

          <div>
            <Label htmlFor="password">Contraseña *</Label>
            <Input id="password" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres" data-testid="partner-password-input" />
          </div>

          <Button
            type="submit"
            disabled={submitting || codeStatus !== 'valid'}
            data-testid="partner-register-submit"
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando cuenta…</> : 'Crear cuenta de partner'}
          </Button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-6">
          ¿Ya tenés cuenta?{' '}
          <button onClick={() => navigate('/login')} className="text-indigo-600 hover:underline font-medium" data-testid="login-link">
            Iniciá sesión
          </button>
        </p>
      </div>
    </div>
  );
}
